/**
 * FederatedRangeFacet is a React component for rendering a range facet with date selection functionality.
 * It allows users to select a date range using dropdowns for day, month, and year,
 * and also provides preset ranges for common date periods such as "Last week" or "This year".
 * 
 * Features:
 * - Supports dynamic date selection through dropdowns for day and month, and a text input for the year.
 * - Preset date ranges for quick selection of common periods.
 * - Localization support for date formats and labels using the `moment` library.
 * - Customizable styles for dropdown components using `react-select`.
 * - Handles input validation for date entries and prevents invalid or incomplete date selections.
 * - Automatically adjusts the day selection based on the selected month.
 * - Provides a reset button to clear all date selections.
 * 
 * Props:
 * - `label` (string): The label displayed for the date facet.
 * - `field` (string): The identifier for the date facet.
 * - `truncateFacetListsAt` (number): Maximum number of items to display in the dropdown (unused but passed as part of props).
 * - `onChange` (function): Callback function triggered when the date range changes. Receives the field name and the selected date range in ISO 8601 format.
 * - `value` (array): The current value of the date range, expected as an array with start and end dates in ISO 8601 format.
 * 
 * State:
 * - `startDate` (moment|null): The selected start date or `null` if not selected.
 * - `endDate` (moment|null): The selected end date or `null` if not selected.
 * - `startDay`, `startMonth`, `startYear` (string): The components of the selected start date.
 * - `endDay`, `endMonth`, `endYear` (string): The components of the selected end date.
 * 
 * Methods:
 * - `resetDateInputs()`: Clears all date inputs and resets the component's state.
 * - `onDatesChange({ startDate, endDate })`: Updates the selected date range and triggers the `onChange` callback.
 * - `handleDateChange(type, value, field)`: Handles changes to individual date inputs (day, month, or year) and updates the state.
 * - `getLastDayInMonth(month)`: Returns the last valid day for a given month.
 * 
 * Usage:
 * <FederatedRangeFacet
 *   label="Date Range"
 *   field="date_range"
 *   onChange={(field, range) => console.log(`Field: ${field}, Range: ${range}`)}
 *   value={[]}
 * />
 */

import PropTypes from "prop-types";
import React from "react";
import moment from "moment";
import 'moment/locale/es'
import 'moment/locale/ru'
import "react-dates/initialize";
import reactT from "./../../components/reactT";
import Select from "react-select";

const langcode = document.querySelector('html').getAttribute('lang');
moment.locale(langcode);
moment.updateLocale(langcode, {
  longDateFormat: {
    L: "MMM DD, YY",
  }
});

// Get current year.
const currentYear = new Date().getFullYear();

// Helper arrays for days and months.
const days = Array.from({ length: 31 }, (_, i) => i + 1);

const monthsOptions = [
  { value: "1", label: reactT("January") },
  { value: "2", label: reactT("February") },
  { value: "3", label: reactT("March") },
  { value: "4", label: reactT("April") },
  { value: "5", label: reactT("May") },
  { value: "6", label: reactT("June") },
  { value: "7", label: reactT("July") },
  { value: "8", label: reactT("August") },
  { value: "9", label: reactT("September") },
  { value: "10", label: reactT("October") },
  { value: "11", label: reactT("November") },
  { value: "12", label: reactT("December") },
];

const daysOptions = days.map((day) => {
  return { value: String(day), label: String(day) };
});

const customStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '32px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '2px',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '2px',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 4px',
  }),
};

class FederatedRangeFacet extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      filter: "",
      truncateFacetListsAt: props.truncateFacetListsAt,
      isClearable: true,
      startDate: null,
      endDate: null,
      startDay: "",
      startMonth: "",
      startYear: "",
      endDay: "",
      endMonth: "",
      endYear: "",
      errorMessage: "",
    };

    this.onDatesChange = this.onDatesChange.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
  }

  /**
   * Resets the startDate and endDate in the state to null if the value prop is empty
   * and both startDate and endDate in the state are not null.
   */
  resetInputs() {
    if (this.props.value.length === 0 && this.state.startDate !== null && this.state.endDate !== null) {
      this.state.startDate = null;
      this.state.endDate = null;
    }
  }

  /**
   * Handler for when the start or end dates change.
   * @param {object} arg - Object with `startDate` and `endDate` properties.
   * @param {object} arg.startDate - The new start date.
   * @param {object} arg.endDate - The new end date.
   * @return {undefined}
   */
  onDatesChange({ startDate, endDate }) {
    this.setState({ startDate, endDate });
    this.props.onChange(this.props.field, [
      moment(startDate).format("YYYY-MM-DDTHH:mm:ss") + "Z",
      moment(endDate).format("YYYY-MM-DDTHH:mm:ss") + "Z"
    ]);
  }

  /**
   * Returns the last day of the given month.
   * @param {string} month - The month for which to get the last day (1-12).
   * @return {string} The last day of the month.
   */
  getLastDayInMonth(month) {
    const monthInt = parseInt(month, 10);
    if (isNaN(monthInt) || monthInt < 1 || monthInt > 12) {
      throw new Error("Invalid month value");
    }

    // Handle February (leap year consideration is not required for this use case).
    if (monthInt === 2) {
      return "29";
    }

    // Handle months with 30 days.
    if ([4, 6, 9, 11].includes(monthInt)) {
      return "30";
    }

    // Handle months with 31 days.
    return "31";
  }

  /**
   * Handles date input changes.
   * @param {string} type - Whether it's the start or end date.
   * @param {string} value - The new value.
   * @param {string} field - The field name.
   * @return {undefined}
   */
  handleDateChange(type, value, field) {
    this.setState({ [`${type}${field}`]: value }, () => {
      const { startDay, startMonth, startYear, endDay, endMonth, endYear } = this.state;

      // Ensure month and day default to 1 if not selected
      let sanitizedStartDay = !startDay ? "1" : startDay;
      let sanitizedStartMonth = !startMonth ? "1" : startMonth;

      let sanitizedEndMonth = !endMonth ? "1" : endMonth;
      let sanitizedEndDay = !endDay ? this.getLastDayInMonth(sanitizedEndMonth) : endDay;

      // In case there are no end day or end month then it will be 31/12/endYear.
      if (!endMonth && !endDay) {
        sanitizedEndMonth = "12";
        sanitizedEndDay = this.getLastDayInMonth(sanitizedEndMonth);
      }

      // In case the day is 31 and the actual last day in the month is 30 or 28.
      if (parseInt(sanitizedStartDay, 10) > this.getLastDayInMonth(sanitizedStartMonth)) {
        sanitizedStartDay = this.getLastDayInMonth(sanitizedStartMonth);
      }

      if (parseInt(sanitizedEndDay, 10) > this.getLastDayInMonth(sanitizedEndMonth)) {
        sanitizedEndDay = this.getLastDayInMonth(sanitizedEndMonth);
      }

      // Only set values after complete the end year.
      if ((!endYear || endYear.length !== 4) || (!startYear || startYear.length !== 4) || (startYear > endYear)) return null;

      const startDate = moment(
        `${startYear}-${sanitizedStartMonth}-${sanitizedStartDay}`,
        "YYYY-MM-DD"
      );

      // Adjust the day for February if the year is not a leap year.
      if (sanitizedEndMonth === "2" && sanitizedEndDay === "29" && !moment([endYear]).isLeapYear()) {
        sanitizedEndDay = "28";
      }

      let endDate = moment(
        `${endYear}-${sanitizedEndMonth}-${sanitizedEndDay}`,
        "YYYY-MM-DD"
      ).endOf('day');

      // If endDate is in the future, set it to today.
      if (endDate.isAfter(moment())) {
        endDate = moment();
      }

      if (type === "start") {
        this.setState({ startDate });
      } else {
        this.setState({ endDate });
      }

      this.props.onChange(this.props.field, [
        startDate.format("YYYY-MM-DDTHH:mm:ss") + "Z",
        endDate.format("YYYY-MM-DDTHH:mm:ss") + "Z"
      ]);
    });
  }

  /**
   * Resets the date inputs in the state to their initial values.
   */
  resetDateInputs() {
    this.setState({
      startDate: null,
      endDate: null,
      startDay: "",
      startMonth: "",
      startYear: "",
      endDay: "",
      endMonth: "",
      endYear: "",
    });

    // Uncheck all preset radio buttons.
    let presetRadios = document.querySelectorAll("input[name='datePreset']");
    presetRadios.forEach((radio) => {
      radio.checked = false;
    });
  }

  /**
 * A functional component that renders preset date range radio buttons.
 * @param {function} onDatesChange - Callback function to update dates when a preset is selected.
 * @return {JSX.Element} A JSX element containing date range preset radio buttons.
 */
  DatePresets = ({ onDatesChange }) => {
    const presets = [
      {
        text: reactT("Last week"), // Covers the last 7 days.
        start: moment().subtract(7, "days").startOf("day"),
        end: moment(),
      },
      {
        text: reactT("Last month"), // Covers the full previous month.
        start: moment().subtract(30, "days").startOf("day"),
        end: moment(),
      },
      {
        text: reactT("Last three months"), // Covers the full previous 3 months.
        start: moment().subtract(90, "days").startOf("day"),
        end: moment(),
      },
      {
        text: reactT("This year"), // From January 1st of the current year to today.
        start: moment().startOf("year"),
        end: moment(),
      },
      {
        text: reactT("Last year"), // Covers the full previous year.
        start: moment().subtract(1, "year").startOf("year"),
        end: moment().subtract(1, "year").endOf("year"),
      },
    ];

    const handleChange = (event) => {
      const selectedPreset = presets.find(
        (preset) => preset.text === event.target.value
      );
      if (selectedPreset) {
        onDatesChange({
          startDate: selectedPreset.start,
          endDate: selectedPreset.end,
        });
      }
    };

    return (
      <div className="presets-wrapper">
        {presets.map(({ text }) => (
          <div key={text} className="preset-radio">
            <input
              type="radio"
              id={`preset-${text}`}
              name="datePreset"
              value={text}
              onChange={handleChange}
            />
            <label htmlFor={`preset-${text}`}>{text}</label>
          </div>
        ))}
      </div>
    );
  };

  render() {
    this.resetInputs();
    const { label, field } = this.props;
    const { DatePresets } = this;

    return (
      <div className={`block block-unhcr-search block-${field}`}>
        <div className="accordion-filter pb-0" id={`accordion-${field}`}>
          <div className="card-facet">
            <div
              className="card-header-facet d-flex align-items-center justify-content-between"
              id={label.replace(/\s+/g, "-").toLowerCase()}
            >
              <div className="section-accordion-title d-flex justify-content-between align-items-center">
                {label}
              </div>
              <span
                className="section-accordion-toggle collapsed"
                role="button"
                data-toggle="collapse"
                data-target={`#collapse-${field}`}
                aria-expanded="false"
                aria-controls={`collapse-${field}`}
                aria-label="Toggle collapse"
              >
              </span>
            </div>

            <div>{this.state.errorMessage}</div>
            <div
              id={`collapse-${field}`}
              className="collapse pb-2"
            >
              <div
                className="card-body-facet"
                key={`solr-list-facet-${field}-ul`}
              >

                <div className="date-wrapper presets">
                  {/* Date Presets */}
                  <DatePresets onDatesChange={this.onDatesChange} />
                </div>

                {/* Start Date Wrapper */}
                <div className="date-wrapper start">
                  <div className="date-label">{reactT("Start date")}</div>
                  <span>{`${reactT("Enter a date like")} 1 ${reactT("January")} 2003, ${reactT("January")} 2003, ${reactT("or just")} 2003`}</span>
                  <div className="date-input-wrapper start">
                    <Select
                      value={daysOptions.find(day => day.value === this.state.startDay) || null}
                      onChange={(selectedOption) => {
                        const selectedStartDayValue = selectedOption ? selectedOption.value : "";
                        this.handleDateChange("start", selectedStartDayValue, "Day")
                      }
                      }
                      options={daysOptions}
                      placeholder={reactT("Day")}
                      isSearchable={true}
                      isClearable={this.state.isClearable}
                      styles={customStyles}
                      className="date-input-day"
                    />

                    <Select
                      value={monthsOptions.find(month => month.value === this.state.startMonth) || null}
                      onChange={(selectedOption) => {
                        const selectedStartMonthValue = selectedOption ? selectedOption.value : "";
                        this.handleDateChange("start", selectedStartMonthValue, "Month")
                      }
                      }
                      options={monthsOptions}
                      placeholder={reactT("Month")}
                      isSearchable={true}
                      isClearable={this.state.isClearable}
                      styles={customStyles}
                      className="date-input-month"
                    />

                    <div className="date-input-year">
                      <input
                        type="number"
                        placeholder={reactT("Year")}
                        value={this.state.startYear}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 4 && !isNaN(value) && value <= currentYear) {
                            this.handleDateChange("start", value, "Year");
                          }
                        }}
                      />

                    </div>
                  </div>
                </div>

                {/* End Date Wrapper */}
                <div className="date-wrapper end">
                  <div className="date-label">{reactT("End date")}</div>
                  <span>{`${reactT("Enter a date like")} 1 ${reactT("January")} ${currentYear}, ${reactT("January")} ${currentYear}, ${reactT("or just")} ${currentYear}`}</span>

                  <div className="date-input-wrapper end">
                    <Select
                      value={daysOptions.find(day => day.value === this.state.endDay) || null}
                      onChange={(selectedOption) => {
                        const selectedEndDayValue = selectedOption ? selectedOption.value : "";
                        this.handleDateChange("end", selectedEndDayValue, "Day");
                      }
                      }
                      options={daysOptions}
                      placeholder={reactT("Day")}
                      isSearchable={true}
                      isClearable={this.state.isClearable}
                      styles={customStyles}
                      className="date-input-day"
                    />

                    <Select
                      value={monthsOptions.find(month => month.value === this.state.endMonth) || null}
                      onChange={(selectedOption) => {
                        const selectedEndMonthValue = selectedOption ? selectedOption.value : "";
                        this.handleDateChange("end", selectedEndMonthValue, "Month");
                      }
                      }
                      options={monthsOptions}
                      placeholder={reactT("Month")}
                      isClearable={this.state.isClearable}
                      isSearchable={true}
                      styles={customStyles}
                      className="date-input-month"
                    />
                    <div className="date-input-year">
                      <input
                        type="number"
                        placeholder={reactT("Year")}
                        value={this.state.endYear}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 4 && !isNaN(value) && value <= currentYear) {
                            this.handleDateChange("end", value, "Year");
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="reset-wrapper">
                  <button
                    type="button"
                    className="hidden reset-date-range"
                    onClick={() => this.resetDateInputs()}
                  >
                    {reactT("Reset")}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div >
    );
  }
}

FederatedRangeFacet.defaultProps = {
  value: [],
};

FederatedRangeFacet.propTypes = {
  bootstrapCss: PropTypes.bool,
  children: PropTypes.array,
  collapse: PropTypes.bool,
  facetSort: PropTypes.string,
  facets: PropTypes.array.isRequired,
  field: PropTypes.string.isRequired,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onFacetSortChange: PropTypes.func,
  onSetCollapse: PropTypes.func,
  query: PropTypes.object,
  truncateFacetListsAt: PropTypes.number,
  value: PropTypes.array,
};

export default FederatedRangeFacet;
